// client/src/components/pages/LexiStar/Declarations/declarationLogic.js
//
// Pure helpers for bulk declarations (T4-S14; LS-09 §5.21; m3-contracts §3.1
// lexistar.declarations): scope kinds, effects, past/future timing, the
// create payload and its validation, and the effect sentence the list shows
// ("23 trips, 41 delay hours reclassified, 7 deviations treated as approved").

import { lineKm } from '../Routes/routeGeometry';
import { fmtNum } from '../StatusBoard/format';

export const SCOPE_KINDS = [
  { value: 'route', label: 'Routes', help: 'Every trip on the chosen routes.' },
  {
    value: 'lane',
    label: 'Lanes',
    help: 'Every trip between the chosen sites, whatever the route.',
  },
  {
    value: 'corridor_segment',
    label: 'Road segment',
    help: 'Draw the stretch of road on the map; trips that pass through it are covered.',
  },
  { value: 'site', label: 'Sites', help: 'Time spent at the chosen sites.' },
  { value: 'fleet', label: 'Trucks', help: 'The chosen trucks, or the whole fleet.' },
  { value: 'vehicle_class', label: 'Vehicle class', help: 'Every truck of the chosen classes.' },
];

export const EFFECTS = [
  {
    value: 'deviation_approved',
    label: 'Deviations treated as approved',
    help: 'Trucks off route inside the scope and period are not flagged as unapproved.',
    reasonKind: 'deviation',
  },
  {
    value: 'delay_reclassified',
    label: 'Delays reclassified as declared',
    help: 'Waiting inside the scope and period counts as a declared delay, not as the driver’s.',
    reasonKind: 'delay',
  },
  {
    value: 'stop_permitted',
    label: 'Stops permitted',
    help: 'Stops inside the scope and period are not unscheduled stops.',
    reasonKind: 'delay',
  },
];

export const effectMeta = (e) => EFFECTS.find((x) => x.value === e) || { label: e || '—' };

export const DEFAULT_CORRIDOR_M = 500;

/** 'future' | 'past' | 'spanning' relative to now — decides advisory vs recompute. */
export function timing(fromIso, toIso, now = Date.now()) {
  const f = fromIso ? new Date(fromIso).getTime() : null;
  const t = toIso ? new Date(toIso).getTime() : null;
  if (f == null || Number.isNaN(f)) return null;
  if (f >= now) return 'future';
  if (t != null && !Number.isNaN(t) && t <= now) return 'past';
  return 'spanning';
}

export const TIMING_TEXT = {
  future:
    'Starts in the future: it goes to the engine as an advisory and applies to trips as they happen.',
  past: 'Wholly in the past: publishing queues a recompute of that period; the change reaches reports when a second user publishes the recompute.',
  spanning:
    'Covers the past and the future: the future part applies as trips happen; the past part is recomputed and published by a second user.',
};

/** Validate the form; returns an error string or null. */
export function validateDeclaration(form) {
  if (!form.effect) return 'Choose the effect.';
  if (!form.scopeKind) return 'Choose the scope.';
  if (form.scopeKind === 'corridor_segment') {
    if (!Array.isArray(form.segment) || form.segment.length < 2)
      return 'Draw the road segment on the map — at least two points.';
    if (!(Number(form.corridorM) >= 50)) return 'The segment needs a corridor of at least 50 m.';
  } else if (form.scopeKind !== 'fleet' && !(form.ids || []).length) {
    return 'Choose what the declaration covers.';
  }
  if (!form.from || !form.to) return 'Set the period.';
  if (new Date(form.to) <= new Date(form.from)) return 'The period must end after it starts.';
  if (!String(form.reason || '').trim())
    return 'Give a reason — it is shown on every affected trip.';
  if (form.withAlt && (!Array.isArray(form.altLine) || form.altLine.length < 2))
    return 'Draw the alternate corridor, or untick it.';
  return null;
}

/** Form → POST /declarations body (026 column names). */
export function buildDeclarationPayload(form) {
  const scope = {
    kind: form.scopeKind,
    ids: form.scopeKind === 'corridor_segment' ? [] : form.ids || [],
  };
  if (form.scopeKind === 'corridor_segment') {
    scope.geometry = { type: 'LineString', coordinates: form.segment };
    scope.corridor_m = Number(form.corridorM);
  }
  const body = {
    scope,
    valid_from: form.from,
    valid_to: form.to,
    effect: form.effect,
    reason_code: form.reasonCode || undefined,
    reason: String(form.reason).trim(),
  };
  if (form.withAlt && form.altLine?.length >= 2) {
    body.alt_corridor = {
      geometry: { type: 'LineString', coordinates: form.altLine },
      corridor_m: Number(form.altCorridorM) || Number(form.corridorM) || DEFAULT_CORRIDOR_M,
    };
  }
  return body;
}

/** Preview / effect_summary → "23 trips · 41 delay hours reclassified · 7 deviations treated as approved". */
export function effectSentence(summary, effect) {
  if (!summary) return null;
  const parts = [];
  const trips = Number(summary.trips);
  if (Number.isFinite(trips)) parts.push(`${fmtNum(trips)} trip${trips === 1 ? '' : 's'}`);
  const hours = Number(summary.delay_hours);
  if (Number.isFinite(hours) && (effect !== 'deviation_approved' || hours > 0))
    parts.push(
      `${fmtNum(hours, hours < 10 && hours % 1 ? 1 : 0)} delay hour${hours === 1 ? '' : 's'} reclassified`
    );
  const devs = Number(summary.deviations);
  if (Number.isFinite(devs) && (effect === 'deviation_approved' || devs > 0))
    parts.push(`${fmtNum(devs)} deviation${devs === 1 ? '' : 's'} treated as approved`);
  return parts.join(' · ');
}

export function scopeText(scope, names = {}) {
  if (!scope) return '—';
  const kind = SCOPE_KINDS.find((k) => k.value === scope.kind);
  if (scope.kind === 'corridor_segment') {
    const km = lineKm(scope.geometry?.coordinates || []);
    return `Road segment · ${fmtNum(km, 1)} km · ${fmtNum(scope.corridor_m)} m wide`;
  }
  if (scope.kind === 'fleet' && !(scope.ids || []).length) return 'Whole fleet';
  const ids = scope.ids || [];
  const named = ids.map((id) => names[String(id)] || String(id).replace(/^reg:/, ''));
  const list =
    named.length <= 3 ? named.join(', ') : `${named.slice(0, 2).join(', ')} +${named.length - 2}`;
  return `${kind?.label || scope.kind} · ${list}`;
}

export const DECLARATION_STATE_META = {
  draft: {
    label: 'Draft',
    glyph: '◌',
    className: 'bg-surface text-slate-600 border border-dashed border-slate-400',
  },
  published: { label: 'Published', glyph: '●', className: 'bg-slate-900 text-white' },
  retracted: {
    label: 'Retracted',
    glyph: '✕',
    className: 'bg-slate-100 text-slate-500 line-through',
  },
};

/** A declaration's liveness: 'active' | 'scheduled' | 'ended' (published only). */
export function liveness(d, now = Date.now()) {
  if (d?.state !== 'published') return null;
  const f = new Date(d.valid_from).getTime();
  const t = new Date(d.valid_to).getTime();
  if (f > now) return 'scheduled';
  if (t < now) return 'ended';
  return 'active';
}
